// react
import { useEffect, useMemo, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// components
import { formatNumber } from '@/utils/formatters';

// relative
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';

import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/no-data-to-display';

const YearOnYearGraph = ({
  data = [], // full SEU array
  targetName = 'Hovedmåler',
  isLoading,
}) => {
  const { analysisType } = useParams({ strict: false });

  const { t } = useTranslation(`energyxplorer-esg-${analysisType || 'power'}`);
  const [showCO2, setShowCO2] = useState(true);

  const chartRef = useRef(null);
  const theme = useTheme();

  /* ────────────────────────────────
   * 1. pick the SEU we want to show
   * ──────────────────────────────── */
  const rows = useMemo(() => {
    const seu = data.find((r) => r.Name === targetName) || null;
    return seu?.Data || [];
  }, [data, targetName]);
  const items = useMemo(
    () => [...rows].sort((a, b) => a.year - b.year || a.month - b.month), // oldest -> newest
    [rows],
  );

  /* ────────────────────────────────
   * 4. build categories + series
   * ──────────────────────────────── */
  const { categories, thisYearData, lastYearData, thisYear, lastYear } =
    useMemo(() => {
      const cats = [];
      const cur = [];
      const prev = [];

      items.forEach((d) => {
        // label e.g. “mar. 24”
        cats.push(
          new Date(d.year, d.month - 1).toLocaleString('da-DK', {
            month: 'short',
            year: '2-digit',
          }),
        );

        if (showCO2) {
          cur.push(d.CO2e / 1000 ?? 0); // THIS YEAR
          prev.push(d.CO2eLastYear / 1000 ?? null); // LAST YEAR already supplied
        } else {
          cur.push(d.Energy ?? 0); // THIS YEAR
          prev.push(d.EnergyLastYear ?? null); // LAST YEAR already supplied
        }
      });

      const latest = items[items.length - 1];
      const first = items[0];

      const lastYearLabel =
        latest?.year === first?.year
          ? latest?.year - 1
          : `${first?.year - 1} - ${latest?.year - 1}`;
      const thisYearLabel =
        first?.year === latest?.year
          ? first?.year
          : `${first?.year} - ${latest?.year}`;

      return {
        categories: cats,
        thisYear: thisYearLabel,
        lastYear: lastYearLabel,
        thisYearData: cur,
        lastYearData: prev,
      };
    }, [items, showCO2]);

  /* ────────────────────────────────
   * 5. Highcharts options
   * ──────────────────────────────── */
  const options = useMemo(
    () => ({
      chart: { type: 'column' },
      title: { text: '' },
      xAxis: {
        categories,
        labels: {
          formatter() {
            return this.value.includes('maj')
              ? this.value.split(' ')[0]
              : this.value.split('.')[0];
          },
        },
        title: { text: t('yoy_bar_chart.month') },
      },
      yAxis: {
        min: 0,
        title: {
          text: showCO2
            ? t('yoy_bar_chart.emission.y_axis_label')
            : t('yoy_bar_chart.consumption.y_axis_label'),
        },
      },

      series: [
        {
          name: `${thisYear}`,
          data: thisYearData,
          tooltip: {
            headerFormat: '',
            pointFormatter() {
              return `<span>${this.category}</span><br/>${t(
                'total',
              )}: <b>${formatNumber(this.y)} ${
                showCO2 ? t('emission_unit') : t('consumption_unit')
              }</b><br/>`;
            },
          },
        },
        {
          name: `${lastYear}`,
          data: lastYearData,
          tooltip: {
            headerFormat: '',
            pointFormatter() {
              return `<span>${this.category.split(' ')[0]} ${
                parseInt(this.category.split(' ')[1]) - 1
              }</span><br/>${t('total')}: <b>${formatNumber(this.y)} ${
                showCO2 ? t('emission_unit') : t('consumption_unit')
              }</b><br/>`;
            },
          },
        },
      ],
    }),
    [categories, t, showCO2, thisYear, thisYearData, lastYear, lastYearData],
  );

  /* ────────────────────────────────
   * 6. theming / loading side-effects
   * ──────────────────────────────── */
  useEffect(() => {
    const themeOpts = theme.palette.mode === 'dark' ? darkTheme : lightTheme;
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.update(
      {
        ...themeOpts,
      },
      false,
    );
    chart.redraw();
  }, [theme]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart) {
      isLoading ? chart.showLoading(t('loading_data')) : chart.hideLoading();
    }
  }, [isLoading, t]);

  /* ────────────────────────────────
   * 7. render
   * ──────────────────────────────── */
  const handleFullscreen = () => chartRef.current.chart.fullscreen.open();
  if (data.length === 0) return null;
  return (
    <Card sx={{ p: 1, borderRadius: 2, minHeight: 260, height: '100%' }}>
      <CardContent>
        <Stack
          component="div"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%' }}
        >
          <Stack
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ width: '100%' }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color="textSecondary"
                >
                  {t('yoy_bar_chart.label')}
                </Typography>
                <InfoTooltipIcon
                  title={
                    showCO2
                      ? t('yoy_bar_chart.emission.description')
                      : t('yoy_bar_chart.consumption.description')
                  }
                />
              </Box>
              <Box className="non-printable">
                <Tooltip
                  title={
                    showCO2
                      ? t('yoy_bar_chart.emission.toggleUnit')
                      : t('yoy_bar_chart.consumption.toggleUnit')
                  }
                  placement="top"
                >
                  <IconButton
                    onClick={() => setShowCO2(!showCO2)}
                    sx={{ borderRadius: 1 }}
                  >
                    {showCO2 ? <SpeedRoundedIcon /> : <Co2RoundedIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <Box className="non-printable">
                <IconButton
                  onClick={handleFullscreen}
                  sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Box>

              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                ref={chartRef}
                containerProps={{ style: { width: '100%', height: '100%' } }}
              />
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default YearOnYearGraph;
